import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { SOURCE_LANGUAGE, TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { isProduction } from 'src/utils/env/isProduction';
import {
  ARABIC_SCRIPT_REPLIT_URL,
  ARABIC_SCRIPT_SOURCE_LANGUAGES,
  ARABIC_SCRIPT_TARGET_LANGUAGES,
} from 'src/utils/replit/shouldRedirectToArabicScriptReplit';
import {
  SKILLS_CHECK_BASE_URL,
  SKILLS_CHECK_LOCALIZED_PATHS,
  SKILLS_CHECK_TARGET_LANGUAGES,
} from 'src/utils/replit/shouldShowSkillsCheckTab';
import {
  CONNECTING_WITH_OTHERS_REPLIT_BASE,
  CONNECTING_WITH_OTHERS_SOURCE_LANGUAGES,
  CONNECTING_WITH_OTHERS_TARGET_TO_PATH,
} from 'src/utils/replit/shouldRedirectToConnectingWithOthersReplit';
import {
  MEMRISE_STORIES_REPLIT_URL,
  MEMRISE_STORIES_SOURCE_LANGUAGES,
  MEMRISE_STORIES_TARGET_LANGUAGES,
} from 'src/utils/replit/shouldRedirectToMemriseStoriesReplit';

export type Prototype = {
  name: string;
  nameMessageId?: string; // Optional i18n message ID for localized name
  description: string;
  descriptionMessageId?: string; // Optional i18n message ID for localized description
  link: string;
  isProd: boolean;
  sourceLanguages: string[]; // SL locales; empty means all
  targetLanguages: string[]; // TL locales; empty means all
  /**
   * Optional path suffixes per locale. Use `localizedLinksBy` to say whether keys are source
   * (e.g. Skills check) or target (e.g. Connecting with others) language codes. Defaults to `source`.
   */
  localizedLinks?: Record<string, string>;
  localizedLinksBy?: 'source' | 'target';
};

/**
 * Resolves the prototype URL, appending a path from `localizedLinks` when present.
 * Lookup key is source or target locale according to `localizedLinksBy` (default `source`).
 */
export const getLocalizedPrototypeLink = (
  prototype: Prototype,
  sourceLanguage: string,
  targetLanguage?: string,
): string => {
  if (!prototype.localizedLinks) {
    return prototype.link;
  }
  const keyLocale = prototype.localizedLinksBy ?? 'source';
  const lookupKey = keyLocale === 'target' ? (targetLanguage ?? '') : sourceLanguage;
  const urlPath = prototype.localizedLinks[lookupKey] ?? '';
  return `${prototype.link}${urlPath}`;
};

type IntlFormatMessage = (descriptor: { id: string; defaultMessage: string }) => string;

/**
 * Gets the localized text for a prototype field.
 * If the prototype has a messageId for the field, it uses intl to format it.
 * Otherwise, it returns the default value.
 */
export const getLocalizedPrototypeText = (
  intl: { formatMessage: IntlFormatMessage },
  messageId: string | undefined,
  defaultValue: string,
): string => {
  if (messageId) {
    return intl.formatMessage({ id: messageId, defaultMessage: defaultValue });
  }
  return defaultValue;
};

/**
 * Checks if a prototype matches the given source and target language pair.
 * Empty language arrays in the prototype match any language.
 */
export const prototypeMatchesLanguagePair = (
  prototype: Prototype,
  sourceLanguage: string,
  targetLanguage: string,
): boolean => {
  const matchesSource =
    prototype.sourceLanguages.length === 0 || prototype.sourceLanguages.includes(sourceLanguage);
  const matchesTarget =
    prototype.targetLanguages.length === 0 || prototype.targetLanguages.includes(targetLanguage);
  return matchesSource && matchesTarget;
};

/**
 * Checks if there are any Labs prototypes available for the current language pair.
 */
export const getIsValidLabsLanguagePair = (): boolean => {
  const sourceLanguage = getLocalStorageEntry(SOURCE_LANGUAGE) ?? '';
  const targetLanguage = getLocalStorageEntry(TARGET_LANGUAGE) ?? '';
  const envFiltered = isProduction() ? LABS_PROTOTYPES.filter(p => p.isProd) : LABS_PROTOTYPES;

  return envFiltered.some(prototype =>
    prototypeMatchesLanguagePair(prototype, sourceLanguage, targetLanguage),
  );
};

export const LABS_PROTOTYPES: Prototype[] = [
  {
    name: 'Prototype (example)', // Veranika
    description: 'This is an example prototype visible for ALL LPs',
    link: 'https://prototyping-test-deployment.replit.app',
    isProd: false,
    sourceLanguages: [],
    targetLanguages: [],
  },
  {
    name: 'Character Writing Practice', // Yan
    description: 'Practice writing Japanese hiraganas and katakanas',
    link: 'https://japanese-writing-practice.replit.app',
    isProd: true,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: ['jp-full', 'jp-romaji'],
  },
  {
    name: 'Flip cards', // Andrei
    description: 'Show flip cards for words you learned',
    link: 'https://prototype-flip-cards.replit.app',
    isProd: false,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: [],
  },
  {
    name: 'Word Nuggets', // Yan
    description:
      'Learn about grammar tips, cultural insights, etymology of the words that you have fully learned!',
    link: 'https://word-nugget.replit.app',
    isProd: true,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: [],
  },
  {
    name: 'My Grammar', // Kasia
    description: 'Learn the grammar behind phrases you already know and use.',
    link: 'https://grammar-v-7-kasiabania.replit.app',
    isProd: true,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: ['it'],
  },
  {
    name: 'Verb Conjugation Practice', // Yan
    description: 'Learn different tenses and practice verb conjugation.',
    link: 'https://verb-conjugation.replit.app',
    isProd: true,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: ['es', 'fr', 'de'],
  },
  {
    name: 'Virtual Tutor', // Yan
    description: 'Practice speaking with your AI tutor through engaging activities.',
    link: 'https://voice-only-mode.replit.app',
    isProd: true,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: ['es', 'es-mx'],
  },
  {
    name: 'New Talk Back Mode', // Fanny
    description: 'Reply with your own words',
    link: 'https://new-talk-back-mode.replit.app',
    isProd: true,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: ['fr'],
  },
  {
    name: 'Arabic Script', // Kasia
    description: 'Everything you need to learn the Arabic script',
    link: ARABIC_SCRIPT_REPLIT_URL,
    isProd: true,
    sourceLanguages: ARABIC_SCRIPT_SOURCE_LANGUAGES,
    targetLanguages: ARABIC_SCRIPT_TARGET_LANGUAGES,
  },
  {
    name: 'Speak for Real', // Kasia
    description:
      'Scenario-based practice that builds you up — from words to full conversation, step by step.',
    link: CONNECTING_WITH_OTHERS_REPLIT_BASE,
    isProd: true,
    sourceLanguages: CONNECTING_WITH_OTHERS_SOURCE_LANGUAGES,
    targetLanguages: Object.keys(CONNECTING_WITH_OTHERS_TARGET_TO_PATH),
    localizedLinks: CONNECTING_WITH_OTHERS_TARGET_TO_PATH,
    localizedLinksBy: 'target',
  },
  {
    name: 'Interactive English Podcasts', // Mhairi
    nameMessageId: 'labs_tab_podcasts_title',
    description:
      'Listen to personalized podcasts, practice speaking with the podcast host, and learn vocabulary you care about',
    descriptionMessageId: 'labs_tab_podcasts_description',
    link: 'https://english-podchats-memrise.replit.app',
    isProd: true,
    sourceLanguages: [],
    targetLanguages: ['en', 'en-us'],
  },
  {
    name: 'Skills check', // Kasia
    nameMessageId: 'exams_tab_name',
    description: 'Perfect for exam prep or understanding your English level more clearly.',
    descriptionMessageId: 'exams_labs_card_description',
    link: SKILLS_CHECK_BASE_URL,
    isProd: true,
    sourceLanguages: Object.keys(SKILLS_CHECK_LOCALIZED_PATHS),
    targetLanguages: SKILLS_CHECK_TARGET_LANGUAGES,
    localizedLinks: SKILLS_CHECK_LOCALIZED_PATHS,
  },
  {
    name: 'Dub Detective', // Lizzie
    description: 'Guess if Spanish video clips are dubbed or original!',
    link: 'https://dub-detective.replit.app',
    isProd: false,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: ['es', 'es-mx'],
  },
  {
    name: 'Article language mix', // Lizzie
    description: 'Turn any article into a bilingual listening lesson.',
    link: 'https://Language-mix.replit.app',
    isProd: false,
    sourceLanguages: ['en', 'en-us'],
    targetLanguages: [],
  },
  {
    name: 'Memrise Stories', // Sandra
    description: 'Learn by reading stories, understand the culture behind them.',
    link: MEMRISE_STORIES_REPLIT_URL,
    isProd: true,
    sourceLanguages: MEMRISE_STORIES_SOURCE_LANGUAGES,
    targetLanguages: MEMRISE_STORIES_TARGET_LANGUAGES,
  },
];
